import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Filter, Clock, Brain, Lightbulb, CheckCircle, RefreshCw, Link, Save, Settings } from 'lucide-react';

interface SearchResult {
  id: string;
  text: string;
  context: string;
  position: {
    startOffset: number;
    endOffset: number;
    xpath: string;
  };
  relevance: number;
  confidence: number;
  type: 'exact' | 'semantic' | 'concept' | 'related';
  highlights: string[];
  suggestions: string[];
  relatedTerms: string[];
  createdAt: Date;
}

interface SearchQuery {
  id: string;
  query: string;
  results: SearchResult[];
  timestamp: Date;
  isSaved: boolean;
  tags: string[];
}

interface ContextSearchProps {
  articleId: string;
  articleContent: string;
  articleTitle: string;
  onSearchResult?: (result: SearchResult) => void;
  onSearchQuery?: (query: SearchQuery) => void;
  className?: string;
}

const ContextSearch: React.FC<ContextSearchProps> = ({
  articleId,
  articleContent,
  articleTitle,
  onSearchResult,
  onSearchQuery,
  className = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [savedQueries, setSavedQueries] = useState<SearchQuery[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'relevance' | 'confidence' | 'position'>('relevance');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchMode, setSearchMode] = useState<'semantic' | 'exact' | 'concept'>('semantic');

  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Mock search suggestions
  const mockSuggestions = [
    'What is React Server Components?',
    'How does WebAssembly work?',
    'What are the benefits of edge computing?',
    'How to optimize web performance?',
    'What is AI-powered development?',
    'How to implement dark mode?',
    'What are the latest web trends?',
    'How to build responsive design?'
  ];

  // Mock search results
  const mockSearchResults: SearchResult[] = [
    {
      id: '1',
      text: 'React Server Components represent one of the most significant shifts in how we think about React applications.',
      context: 'Unlike traditional client-side components, RSCs run on the server, enabling developers to build more performant applications with better SEO and faster initial page loads.',
      position: {
        startOffset: 0,
        endOffset: 0,
        xpath: '/html/body/div[1]/p[1]'
      },
      relevance: 0.95,
      confidence: 0.92,
      type: 'exact',
      highlights: ['React Server Components', 'significant shifts', 'React applications'],
      suggestions: ['What are React Server Components?', 'How do RSCs work?', 'Benefits of RSCs'],
      relatedTerms: ['RSC', 'Server Components', 'React', 'Performance', 'SEO'],
      createdAt: new Date()
    },
    {
      id: '2',
      text: 'WebAssembly (WASM) is revolutionizing web performance by allowing developers to run code written in languages like C, C++, and Rust directly in the browser.',
      context: 'This opens up new possibilities for computationally intensive applications that were previously impossible to run efficiently in web browsers.',
      position: {
        startOffset: 0,
        endOffset: 0,
        xpath: '/html/body/div[1]/p[2]'
      },
      relevance: 0.88,
      confidence: 0.89,
      type: 'semantic',
      highlights: ['WebAssembly', 'WASM', 'web performance', 'C, C++, and Rust'],
      suggestions: ['What is WebAssembly?', 'How does WASM work?', 'WASM vs JavaScript'],
      relatedTerms: ['WASM', 'WebAssembly', 'Performance', 'C++', 'Rust', 'Browser'],
      createdAt: new Date()
    }
  ];

  // Perform search
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setShowResults(true);

    // Simulate AI search processing
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Filter results based on query
    const filteredResults = mockSearchResults.filter(result =>
      result.text.toLowerCase().includes(query.toLowerCase()) ||
      result.context.toLowerCase().includes(query.toLowerCase()) ||
      result.highlights.some(highlight => 
        highlight.toLowerCase().includes(query.toLowerCase())
      )
    );

    // Sort results
    const sortedResults = filteredResults.sort((a, b) => {
      switch (sortBy) {
        case 'relevance':
          return b.relevance - a.relevance;
        case 'confidence':
          return b.confidence - a.confidence;
        case 'position':
          return a.position.startOffset - b.position.startOffset;
        default:
          return 0;
      }
    });

    setSearchResults(sortedResults);

    // Save search query
    const searchQueryObj: SearchQuery = {
      id: Date.now().toString(),
      query,
      results: sortedResults,
      timestamp: new Date(),
      isSaved: false,
      tags: []
    };

    setSavedQueries(prev => [searchQueryObj, ...prev.slice(0, 9)]);
    setSearchHistory(prev => [query, ...prev.filter(h => h !== query).slice(0, 9)]);
    onSearchQuery?.(searchQueryObj);

    setIsSearching(false);
  }, [sortBy, onSearchQuery]);

  // Handle search input
  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.length > 2) {
      const filteredSuggestions = mockSuggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(searchQuery);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    performSearch(suggestion);
  };

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    setSelectedResult(result);
    onSearchResult?.(result);
    
    // Scroll to result in content
    try {
      const xpathResult = document.evaluate(
        result.position.xpath,
        document,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null
      );
      
      const element = xpathResult.singleNodeValue as HTMLElement;
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.backgroundColor = '#fef3c7';
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 3000);
      }
    } catch (error) {
      console.error('Error scrolling to result:', error);
    }
  };

  // Filter results by type
  const filteredResults = searchResults.filter(result => {
    if (filterType === 'all') return true;
    return result.type === filterType;
  });

  // Get result type icon
  const getResultTypeIcon = (type: string) => {
    switch (type) {
      case 'exact': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'semantic': return <Brain className="w-4 h-4 text-blue-500" />;
      case 'concept': return <Lightbulb className="w-4 h-4 text-yellow-500" />;
      case 'related': return <Link className="w-4 h-4 text-purple-500" />;
      default: return <Search className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get result type color
  const getResultTypeColor = (type: string) => {
    switch (type) {
      case 'exact': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'semantic': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-400';
      case 'concept': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-400';
      case 'related': return 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  return (
    <div className={`context-search ${className}`}>
      {/* Search Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-500" />
              Context Search
            </h3>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
          
          {/* Search Input */}
          <form onSubmit={handleSearchSubmit} className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchInput}
                placeholder="Search within article..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                </div>
              )}
            </div>
            
            {/* Search Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-white"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </form>
          
          {/* Search History */}
          {searchHistory.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Recent Searches</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {searchHistory.slice(0, 5).map((query, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSearchQuery(query);
                      performSearch(query);
                    }}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Advanced Options */}
          {showAdvanced && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search Mode
                </label>
                <select
                  value={searchMode}
                  onChange={(e) => setSearchMode(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="semantic">Semantic Search</option>
                  <option value="exact">Exact Match</option>
                  <option value="concept">Concept Search</option>
                </select>
              </div>
              
              <div className="flex gap-2">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="exact">Exact Matches</option>
                  <option value="semantic">Semantic</option>
                  <option value="concept">Concepts</option>
                  <option value="related">Related</option>
                </select>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="relevance">Relevance</option>
                  <option value="confidence">Confidence</option>
                  <option value="position">Position</option>
                </select>
              </div>
            </div>
          )}
        </div>
        
        {/* Search Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {searchResults.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {filteredResults.length} results found
                </span>
                <button
                  onClick={() => setSearchResults([])}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Clear
                </button>
              </div>
              
              {filteredResults.map(result => (
                <div
                  key={result.id}
                  className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={() => handleResultClick(result)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getResultTypeIcon(result.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getResultTypeColor(result.type)}`}>
                          {result.type}
                        </span>
                        <span className="text-xs text-gray-500">
                          {Math.round(result.relevance * 100)}% relevant
                        </span>
                        <span className="text-xs text-gray-500">
                          {Math.round(result.confidence * 100)}% confident
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-900 dark:text-white mb-2">
                        {result.text}
                      </p>
                      
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                        {result.context}
                      </p>
                      
                      {/* Highlights */}
                      {result.highlights.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {result.highlights.map((highlight, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded text-xs"
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Related Terms */}
                      {result.relatedTerms.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {result.relatedTerms.map((term, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs"
                            >
                              #{term}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No search results</p>
              <p className="text-sm">Try searching for keywords or concepts</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextSearch;
